/**
 * The weather-map basemap: which vector style both map modes load and which
 * of its layers are noise on a weather map.
 *
 * Pure and dependency-free (no maplibre types): the webapp applies the hide
 * list to a live GL JS map (`components/map/basemapReference.ts`).
 */

// OpenFreeMap "positron" style — a muted, keyless vector basemap built as a
// data backdrop (shaded relief + water, minimal everything else). Our
// vendored copies of it and of OpenFreeMap dark (`./basemaps`, #343) take
// over from this URL in the apps (#344); after that it only names the
// upstream the light document comes from (`scripts/vendor-basemaps.mjs`
// fetches it).
export const WEATHER_BASEMAP_STYLE_URL =
  'https://tiles.openfreemap.org/styles/positron';

/**
 * OpenFreeMap's required credit, for its tiles, the OpenMapTiles schema and
 * the OpenStreetMap data, shown as visible text wherever a map draws this
 * basemap without the GL JS attribution control (the webapp warning map, every
 * mobile MapLibre map). Not translated: these are names, and the OSM licence
 * asks for the credit as written.
 */
export const BASEMAP_ATTRIBUTION =
  '© OpenFreeMap © OpenMapTiles © OpenStreetMap contributors';

// Basemap layers whose whole `source-layer` is noise on a weather map: roads,
// route-number shields + road names (the clutter that appeared on zoom),
// rivers, and airport markers. Hidden wholesale by source-layer so this
// survives the basemap renaming individual layers.
export const BASEMAP_CLUTTER_SOURCE_LAYERS: ReadonlySet<string> = new Set([
  'transportation', // road/rail/pier geometry
  'transportation_name', // road names + route-number shields
  'waterway', // rivers, canals, streams
  'aerodrome_label', // airport markers
]);

// What /map hides: the clutter plus the basemap's own boundary layers. Borders
// are redrawn by the reference layers (`referenceLayerSpecs`) so the look is
// identical across styles — different styles name and stage their boundary
// layers inconsistently.
export const HIDDEN_SOURCE_LAYERS: ReadonlySet<string> = new Set([
  ...BASEMAP_CLUTTER_SOURCE_LAYERS,
  'boundary',
]);

/** The part of a style document these helpers read. */
export type BasemapStyleDoc = {
  sources?: Record<string, unknown>;
  layers?: {id: string; type?: string; 'source-layer'?: string}[];
};

/**
 * Ids of the layers to hide (visibility `none`): the vendor clutter and the
 * native borders, in style order. Empty while the style is not loaded.
 */
export const basemapClutterLayerIds = (
  style: BasemapStyleDoc | null | undefined,
): string[] =>
  (style?.layers ?? [])
    .filter((layer) => {
      const sourceLayer = layer['source-layer'];
      return sourceLayer !== undefined && HIDDEN_SOURCE_LAYERS.has(sourceLayer);
    })
    .map((layer) => layer.id);

/**
 * Id of the first place-label layer. The reference set (coast + borders) and
 * line art anchor just BELOW it, so city names stay readable on top; raster
 * data goes under the reference band instead (`dataAnchorLayerId`).
 * Undefined (top of stack) while the style is still loading or has no place
 * labels.
 */
export const placeLabelAnchorLayerId = (
  style: BasemapStyleDoc | null | undefined,
): string | undefined =>
  (style?.layers ?? []).find((layer) => layer['source-layer'] === 'place')?.id;
